import { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import axios from '../../plugins/axios';
import Toast from '../../plugins/useToast';
import { useTranslation } from 'react-i18next';
import { mmHgToKPa, celsiusToFahrenheit, percentTommHg, percentToKpa, mmHgToPercent, checkEmptyValue, valueStringForPlayer } from '../../plugins/common';
import { useRecoilState } from 'recoil'
import { alarmReviewSelectedTimeState, selectBedInfoState, unitState } from '../../plugins/recoil'

import GraphicTrend from './GraphicTrend';
import AlarmReviewChart from './AlarmReviewChart';
import ReviewPlayer from './ReviewPlayer';
import PrintModal from './PrintModal';

import {ReactComponent as IconRight} from '../../assets/icons/ic-calendar-right.svg'
import {ReactComponent as IconLeft} from '../../assets/icons/ic-calendar-left.svg'
import {ReactComponent as IconLeftEnd} from '../../assets/icons/ic-arrow-left-end.svg'
import {ReactComponent as IconRightEnd} from '../../assets/icons/ic-arrow-right-end.svg'
import {ReactComponent as IconPlay} from '../../assets/icons/ic-play.svg'
import {ReactComponent as IconPrint} from '../../assets/icons/ic-print.svg'
import {ReactComponent as IconArrowUp} from '../../assets/icons/ic-arrow-up.svg'
import {ReactComponent as IconArrowDown} from '../../assets/icons/ic-arrow-down.svg'
import {ReactComponent as IconCheck} from '../../assets/icons/ic-check_blue.svg';

function Index({isAlarmFull, trendWaveData, trendReviewData, selectedAlarmInfo, waveStartTime, waveEndTime, handleChangeSpeed, handlePrev, handleNext, handlePrevEnd, handleNextEnd }) {
    const { t } = useTranslation()
    const speedDropdownRef = useRef(null);
    const startTimeRef = useRef(null);
    const endTimeRef = useRef(null);
    const [showPlayer, setShowPlayer] = useState(false)
    const [isSpeedDropdownOpen, setIsSpeedDropdownOpen] = useState(false);
    const [selectedTime, setSelectedTime] = useRecoilState(alarmReviewSelectedTimeState)
    const [selectBedInfo, setSelectBedInfo] = useRecoilState(selectBedInfoState)
    const [speed, setSpeed] = useState({ label: '25mm/s', value: 25 })
    const [vitals, setVitals] = useState({})
    const [units, setUnits] = useRecoilState(unitState)
    const [showPrint, setShowPrint] = useState(false)
    const isV20 = selectBedInfo?.deviceModel === 'V20'
    const vitalArr = selectBedInfo?.deviceModel === 'V20' ? ['HR/PR', 'SpO2', 'Temp', 'RESP', 'NIBP', 'IBP', 'EtCO2', 'InCO2', 'IPI' ] : ['HR', 'PR', 'SpO2', 'RESP', 'awRR', 'NIBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4', 'EtCO2', 'InCO2', 'Temp1', 'Temp2', 'IPI', 'GasCo2', 'GasO2', 'GasN2O', 'GasDes', 'GasSev']
    const valueStringKeys = { ...valueStringForPlayer }
    if (isV20) {
        valueStringKeys['RESP'] = 'respRate'
        valueStringKeys['InCO2'] = 'fiCo2'
    }

    const speedOptions = [
        { label: '12.5mm/s', value: 12.5 },
        { label: '25mm/s', value: 25 },
        { label: '50mm/s', value: 50 }
    ];

    const prevDisabled = waveStartTime && moment(waveStartTime).diff(moment(selectedAlarmInfo.startTime).subtract(15, 'seconds'), 'seconds') <= 0
    const nextDisabled = waveEndTime && moment(selectedAlarmInfo.endTime).add(15, 'seconds').diff(moment(waveEndTime), 'seconds') <= 0

    const parseVitals = (data) => {
        const newVitals = { ...data }
        

        const bpSeries = ['NIBP', 'IBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4']
        bpSeries.forEach((item) => {
            if (units[item] === 'kPa') {
                newVitals[`${item.toLowerCase()}Sys`] = mmHgToKPa(newVitals[`${item.toLowerCase()}Sys`])
                newVitals[`${item.toLowerCase()}Dia`] = mmHgToKPa(newVitals[`${item.toLowerCase()}Dia`])
                newVitals[`${item.toLowerCase()}Map`] = mmHgToKPa(newVitals[`${item.toLowerCase()}Map`])
            }
        }) 

        const co2Series = ['EtCO2', 'InCO2']
        co2Series.forEach((item) => {
            if (units[item] === 'kPa') {
                newVitals[valueStringKeys[item]] = mmHgToKPa(newVitals[valueStringKeys[item]])
            }else if (units[item] === '%') {
                newVitals[valueStringKeys[item]] = mmHgToPercent(newVitals[valueStringKeys[item]])
            }
        })

        const tempSeries = ['Temp', 'Temp1', 'Temp2']
        tempSeries.forEach(item => {
            if (units[item] === '°F') {
                newVitals[valueStringKeys[item]] = celsiusToFahrenheit(newVitals[valueStringKeys[item]])
            } else {
                if (isNaN(parseFloat(newVitals[valueStringKeys[item]])?.toFixed(1))) {
                    newVitals[valueStringKeys[item]] = null
                } else {
                    newVitals[valueStringKeys[item]] = parseFloat(newVitals[valueStringKeys[item]])?.toFixed(1)
                }
            }
        })

        if (units['GAS'] === 'mmHg') {
            newVitals[valueStringKeys['GasO2Ex']] = percentTommHg(newVitals[valueStringKeys['GasO2Ex']])
            newVitals[valueStringKeys['GasO2In']] = percentTommHg(newVitals[valueStringKeys['GasO2In']])
            newVitals[valueStringKeys['GasCo2Ex']] = percentTommHg(newVitals[valueStringKeys['GasCo2Ex']])
            newVitals[valueStringKeys['GasCo2In']] = percentTommHg(newVitals[valueStringKeys['GasCo2In']])
            newVitals[valueStringKeys['GasSevEx']] = percentTommHg(newVitals[valueStringKeys['GasSevEx']])
            newVitals[valueStringKeys['GasSevIn']] = percentTommHg(newVitals[valueStringKeys['GasSevIn']])
            newVitals[valueStringKeys['GasDesEx']] = percentTommHg(newVitals[valueStringKeys['GasDesEx']])
            newVitals[valueStringKeys['GasDesIn']] = percentTommHg(newVitals[valueStringKeys['GasDesIn']])
            newVitals[valueStringKeys['GasN2OEx']] = percentTommHg(newVitals[valueStringKeys['GasN2OEx']])
            newVitals[valueStringKeys['GasN2OIn']] = percentTommHg(newVitals[valueStringKeys['GasN2OIn']])
        } else if (units['GAS'] === 'kPa') {
            newVitals[valueStringKeys['GasO2Ex']] = percentToKpa(newVitals[valueStringKeys['GasO2Ex']])
            newVitals[valueStringKeys['GasO2In']] = percentToKpa(newVitals[valueStringKeys['GasO2In']])
            newVitals[valueStringKeys['GasCo2Ex']] = percentToKpa(newVitals[valueStringKeys['GasCo2Ex']])
            newVitals[valueStringKeys['GasCo2In']] = percentToKpa(newVitals[valueStringKeys['GasCo2In']])
            newVitals[valueStringKeys['GasSevEx']] = percentToKpa(newVitals[valueStringKeys['GasSevEx']])
            newVitals[valueStringKeys['GasSevIn']] = percentToKpa(newVitals[valueStringKeys['GasSevIn']])
            newVitals[valueStringKeys['GasDesEx']] = percentToKpa(newVitals[valueStringKeys['GasDesEx']])
            newVitals[valueStringKeys['GasDesIn']] = percentToKpa(newVitals[valueStringKeys['GasDesIn']])
            newVitals[valueStringKeys['GasN2OEx']] = percentToKpa(newVitals[valueStringKeys['GasN2OEx']])
            newVitals[valueStringKeys['GasN2OIn']] = percentToKpa(newVitals[valueStringKeys['GasN2OIn']])
        }
        setVitals(newVitals)
    }

    const getVitals = async () => {
        try {
            const res = await axios.get(`/trend/view/vitals?serialNo=${selectedAlarmInfo.serialNo}&eventTime=${moment.utc(selectedTime).format('YYYY-MM-DDTHH:mm:ss')}Z`)
            if (res.code != '200') throw res
            parseVitals(res.data.data[0])
        } catch (err) { console.warn(err) }
    }

    const getV20Vitals = async () => {
        try {
            const res = await axios.get(`/trend/hl7/disconnect/vitals?startTime=${moment.utc(selectedTime).format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(selectedTime).format('YYYY-MM-DDTHH:mm:ss')}Z&device=V20`)
            parseVitals(res.data.datas[0])
        } catch (err) { console.warn(err) }
    }

    // ✅ Alarm Print
    const handleAlarmPrint = (waveParameters) => {
        try{
            setShowPrint(false);            
            const deviceId = selectBedInfo?.deviceId || '';
            const patientId = selectedAlarmInfo?.patientId || '';
            const startTime = `${moment.utc(selectedAlarmInfo?.utcStartTime || '').format('YYYY-MM-DDTHH:mm:ss')}Z`;
            const endTime = `${moment.utc(selectedAlarmInfo?.utcEndTime || '').format('YYYY-MM-DDTHH:mm:ss')}Z`;
            const serialNo = selectedAlarmInfo?.serialNo || '';
            const eventMessage = selectedAlarmInfo?.eventMessage || '';
            const waves = encodeURIComponent(JSON.stringify(waveParameters.map(item => ({'measureParameter' : item.measureParameter}))));            

            const windowFeatures = "width=1200,height=800,left=100,top=100,scrollbars=yes,resizable=yes";      
            const setUrl = `${window.location.origin}/print/alarmform?deviceid=${deviceId}&patientid=${patientId}&starttime=${startTime}&endtime=${endTime}&serialno=${serialNo}&eventmessage=${eventMessage}&wavesinfo=${waves}`;
            const newWindow = window.open(setUrl, "popupWindow", windowFeatures);
            if (newWindow) {
                newWindow.focus();
            } else {
                Toast({ isSuccess: false , msg : t('error.failOpenPage') });
            }
            // console.log('setUrl', setUrl);
        }
        catch(err){
            console.warn(err)
        }
    }

    useEffect(() => {
        setIsSpeedDropdownOpen(false)
        startTimeRef.current = selectedAlarmInfo.startTime;
        endTimeRef.current = selectedAlarmInfo.endTime;
        if (isV20) {
            setSelectedTime(moment(selectedAlarmInfo.startTime))
        }
    }, [ selectedAlarmInfo ]);

    useEffect(() => {
        if (selectedTime) {
            if (isV20) {
                getV20Vitals()
            } else {
                getVitals()
            }
        }
    }, [selectedTime])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (speedDropdownRef.current && !speedDropdownRef.current.contains(e.target)) {
                setIsSpeedDropdownOpen(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    return (
        <>
            <div className="inset-0 bg-black flex flex-col z-[4]" style={{ height: 'calc(100% - 64px)' }}>
                {
                    selectedAlarmInfo && selectedAlarmInfo.startTime && selectedAlarmInfo.endTime &&
                    <>
                        <div className="flex justify-center h-[32px] bg-[#1F1F1F]" style={{ minHeight: 32, maxHeight: 32 }}>
                            <div className="text-[16px] min-w-[400px] text-center font-normal pt-[4px]">
                                <span className="mr-2" style={{ color: selectedAlarmInfo.eventPriority == 2 ? '#FF002E' : (selectedAlarmInfo.eventPriority === 1 || selectedAlarmInfo.eventPriority === 0 )  ? '#FFF500' : '#FFFFFF' }}>{selectedAlarmInfo.eventMessage || 'No eventName'}</span>
                                <span className="">{`(${checkEmptyValue(selectedAlarmInfo.startTime)} ~ ${checkEmptyValue(selectedAlarmInfo.endTime)})`}</span>
                            </div>
                        </div>
                        <div className="scrollbar" style={{ overflowX: 'auto', height: 'calc(100% - 32px)', background: isV20 ? '#2e2e2e' : '' }}>
                            <div className="h-[36px] flex items-center justify-between pr-[8px] gap-[8px]">
                                <div className="flex-1"></div>
                                {
                                    !isV20 &&
                                    <div className="flex h-[26px] w-[132px] px-3 rounded-[888px] gap-3 bg-[#2C2C2C] items-center">
                                        
                                        <div className="cursor-pointer flex items-center justify-center" onClick={() => { if (!prevDisabled) handlePrevEnd() }} style={{ opacity: prevDisabled ? 0.3 : 1 }}>
                                            <IconLeftEnd />
                                        </div>
                                        <div className="cursor-pointer flex items-center justify-center" onClick={() => { if (!prevDisabled) handlePrev() }} style={{opacity: prevDisabled ? 0.3 : 1 }}>
                                            <IconLeft />
                                        </div>
                                        <div className="cursor-pointer flex items-center justify-center" onClick={() => { if (!nextDisabled) handleNext() }} style={{ opacity: nextDisabled ? 0.3 : 1 }}>
                                            <IconRight />
                                        </div>
                                        <div className="cursor-pointer flex items-center justify-center" onClick={() => { if (!nextDisabled) handleNextEnd() }} style={{ opacity: nextDisabled ? 0.3 : 1 }}>
                                            <IconRightEnd />
                                        </div>
                                    </div>
                                }
                                <div className="flex flex-1 justify-end">
                                    <div className="flex gap-[12px]">
                                        {
                                            !isV20 &&
                                            <>
                                                <div className="relative top-[1px]" ref={speedDropdownRef}>
                                                    <div
                                                        className="bg-gradient-to-b from-[#5E5E5E] to-[#383838] text-white w-[126px] h-[28px] px-[8px] rounded-[6px] cursor-pointer flex items-center justify-between border border-[#5E5E5E]"
                                                        onClick={() => setIsSpeedDropdownOpen(!isSpeedDropdownOpen)}
                                                        style={{ boxShadow: "0px 4px 4px 0px #00000066" }}
                                                    >
                                                        <div style={{ fontWeight: 300 }}>{speed.label}</div>
                                                    </div>
                                                    {isSpeedDropdownOpen && (
                                                        <div className="absolute top-[30px] bg-[#1F1F1F] w-[126px] py-[8px] rounded-[6px] border border-[#5E5E5E] shadow-[0px_12px_12px_0px_rgba(0,0,0,0.4)] z-20">
                                                            {speedOptions.map((item, index) => (
                                                                <div key={`speed-type-${index}`} className={`bg-[#1F1F1F] cursor-pointer h-[32px] flex items-center justify-between px-[8px] hover:bg-[#2F2F2F] ${speed.value === item.value ? "text-[#3182F7]" : "text-white"}`} onClick={(e) => { e.stopPropagation(); setSpeed(item); setIsSpeedDropdownOpen((prev) => !prev); handleChangeSpeed(item.value)}}>
                                                                    {item.label}
                                                                    {speed.value === item.value && <IconCheck className="w-4 h-4 ml-2" />}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="absolute transform -translate-y-1/2 pointer-events-none top-1/2 right-2">
                                                        {isSpeedDropdownOpen ? <IconArrowUp /> : <IconArrowDown />}
                                                    </div>
                                                </div>
                                                <div className="w-[1px] h-full bg-[#444444]" />
                                                <div onClick={() => { setShowPlayer(true) }} className="w-[100px] h-[28px] cursor-pointer font-light flex items-center justify-center bg-gradient-to-b from-[#5E5E5E] to-[#383838] border border-[#5E5E5E] shadow-[0px_12px_12px_0px_rgba(0,0,0,0.4)]" style={{ borderRadius: 6 }}>
                                                    {t('alarmReview.replay')}
                                                    <IconPlay className="ml-2" />
                                                </div>
                                            </>
                                        }
                                        <div onClick={() => { setShowPrint(true) }} className="w-[28px] h-[28px] cursor-pointer flex items-center justify-center bg-gradient-to-b from-[#5E5E5E] to-[#383838] border border-[#5E5E5E] shadow-[0px_12px_12px_0px_rgba(0,0,0,0.4)]" style={{ borderRadius: 4 }}>
                                            <IconPrint />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {
                                !isV20 &&
                                <div className='' style={{ height: selectedTime ? 'calc(100% - 36px - 167px)' : 'calc(100% - 36px)' }}>
                                    <div className='h-full'>
                                        <AlarmReviewChart isAlarmFull={isAlarmFull} speed={speed} selectedAlarmInfo={selectedAlarmInfo} waveStartTime={waveStartTime} waveEndTime={waveEndTime} />
                                    </div>
                                </div>
                            }
                            {
                                selectedTime &&
                                <div className='bg-[#2F2F2F] w-full' style={{ minHeight: isV20 ? 107: 167, maxHeight: isV20 ? 107: 167, marginTop: isV20 ? 40 : 0 }}>
                                    <div className="px-5 py-2 h-full gap-[6px] flex flex-col text-[#DEDEDE]">
                                        <div className="font-light" style={{ fontSize: 16 }}>{moment(selectedTime).format('YYYY-MM-DD HH:mm:ss')}</div>
                                        <div className="flex-1 flex flex-wrap" style={{ border: '1px solid #5E5E5E', borderRadius: 8 }}>
                                            {
                                                vitalArr.map((param, idx) => {
                                                    return (
                                                        <div key={`vitals-${idx}`} className="flex font-light" style={{ minHeight: 30, maxHeight: 30, borderBottom: '1px solid #363636', width: '20%' }}>
                                                            {
                                                                ['GasCo2', 'GasO2', 'GasN2O', 'GasDes', 'GasSev'].includes(param) ?
                                                                <div className="h-full px-2 flex items-center" style={{ minWidth: 120, maxWidth: 120 }}>{`${param} (Ex/In)`}</div> :
                                                                <div className="h-full px-2 flex items-center" style={{ minWidth: 120, maxWidth: 120 }}>{param}</div>
                                                            }
                                                            {
                                                                ['HR', 'SpO2', 'RESP', 'awRR', 'EtCO2', 'InCO2', 'Temp', 'Temp1', 'Temp2', 'PR', 'HR/PR', 'IPI'].includes(param) ? 
                                                                <div className="h-full px-2 flex items-center bg-[#1F1F1F] flex-1" style={{ minWidth: 171 }}>{`${checkEmptyValue(vitals[valueStringKeys[param]])} ${units[param]}`}</div> :
                                                                ['NIBP', 'IBP', 'IBP1', 'IBP2', 'IBP3', 'IBP4'].includes(param) ?
                                                                <div className="h-full px-2 flex items-center bg-[#1F1F1F] flex-1" style={{ minWidth: 171 }}>
                                                                    {`${checkEmptyValue(vitals[valueStringKeys[`${param}Sys`]])}/${checkEmptyValue(vitals[valueStringKeys[`${param}Dia`]])}/${checkEmptyValue(vitals[valueStringKeys[`${param}Map`]])} ${units[param]}`}
                                                                </div> :
                                                                <div className="h-full px-2 flex items-center bg-[#1F1F1F] flex-1" style={{ minWidth: 171 }}>
                                                                    {`${checkEmptyValue(vitals[valueStringKeys[`${param}Ex`]])}/${checkEmptyValue(vitals[valueStringKeys[`${param}In`]])} ${units['GAS']}`}
                                                                </div>
                                                            }
                                                        </div>
                                                    )
                                                }
                                            )}
                                        </div>
                                    </div>
                                </div>
                            }
                        </div>
                    </>
                }
            </div>
            {
                showPlayer &&
                <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-[999]">
                    <ReviewPlayer selectedAlarmInfo={selectedAlarmInfo} handleClose={() => { setShowPlayer(false) }}/>
                </div>
            }
            {
                showPrint &&
                <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-[999]">
                    <PrintModal handleAlarmPrint={handleAlarmPrint} handleClose={() => { setShowPrint(false) }} />
                </div>
            }
        </>
    )
}

export default Index;